import React, { useEffect, useState, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Link, useNavigate } from "react-router-dom";
import {
  DatatableWrapper,
  TableHeader,
  TableBody,
  Pagination,
  PaginationOptions,
  Filter,
} from "react-bs-datatable";
import "bootstrap/dist/css/bootstrap.min.css";
import { Button, Badge } from "react-bootstrap";
import {
  fetchCategories,
  deleteCategory,
  updateCategoryStatus,
  clearError,
  clearSuccess,
} from "../../redux/slices/categorySlice";
import CategoryModal from "./CategoryModal";
import InfoPill from "../Common/InfoPill";
import ConfirmModal from "../Common/ConfirmModal";
import { FaPencilAlt, FaRegTrashAlt, FaEye } from "react-icons/fa";
import { toast } from "react-toastify";

const Category = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  // 🌟 सुधारित: Redux state को अलग-अलग चुनें ताकि अनावश्यक री-रेंडरिंग न हो
  const categories = useSelector((state) => state.categories.categories) || [];
  const isLoading = useSelector((state) => state.categories.isLoading);
  const error = useSelector((state) => state.categories.error);
  const success = useSelector((state) => state.categories.success);
  const isDeleting = useSelector((state) => state.categories.isDeleting);
  const isUpdating = useSelector((state) => state.categories.isUpdating);
  // ----------------------------------------------------------------------

  const [showModal, setShowModal] = useState(false);
  const [editingCategory, setEditingCategory] = useState(null);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [categoryToDelete, setCategoryToDelete] = useState(null);

  // Use refs to track if we've already shown toasts for current messages
  const lastErrorRef = useRef(null);
  const lastSuccessRef = useRef(null);

  useEffect(() => {
    dispatch(fetchCategories());
    // Clear any existing messages when component mounts
    dispatch(clearError());
    dispatch(clearSuccess());
    // Reset refs on mount to allow new toasts
    lastErrorRef.current = null;
    lastSuccessRef.current = null;
  }, [dispatch]);

  // Clear messages and show toast notifications
  useEffect(() => {
    if (error) {
      // Handle both string and object error messages
      const errorMessage =
        typeof error === "string"
          ? error
          : error.message || "An error occurred";
      toast.error(errorMessage);
      // Clear error immediately after showing toast
      dispatch(clearError());
    }
  }, [error, dispatch]);

  useEffect(() => {
    if (success) {
      toast.success(success);
      // Clear success immediately after showing toast
      dispatch(clearSuccess());
    }
  }, [success, dispatch]);

  // Cleanup: Clear messages when component unmounts
  useEffect(() => {
    return () => {
      dispatch(clearError());
      dispatch(clearSuccess());
      lastErrorRef.current = null;
      lastSuccessRef.current = null;
    };
  }, [dispatch]);

  const handleEditCategory = (categoryId) => {
    setEditingCategory(categoryId);
    setShowModal(true);
  };

  const handleCreateCategory = () => {
    setEditingCategory(null);
    setShowModal(true);
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setEditingCategory(null);
    // 🟢 सुधारित: अनावश्यक API कॉल और री-रेंडरिंग से बचने के लिए हटा दिया गया।
    // dispatch(fetchCategories());
  };

  const handleDeleteCategory = (categoryId, categoryName) => {
    setCategoryToDelete({ id: categoryId, name: categoryName });
    setShowConfirmModal(true);
  };

  const confirmDeleteCategory = async () => {
    if (categoryToDelete) {
      try {
        await dispatch(deleteCategory(categoryToDelete.id)).unwrap();
        // Delete के बाद fetch करना अक्सर pagination/filtering को सही करने के लिए आवश्यक होता है
        dispatch(fetchCategories());
        setShowConfirmModal(false);
        setCategoryToDelete(null);
        // Toast will be shown by useEffect based on Redux success state
      } catch (error) {
        console.error("Failed to delete category:", error);
        // Toast will be shown by useEffect based on Redux error state
        // Keep the modal open so user can see the error
      }
    }
  };

  const cancelDeleteCategory = () => {
    setShowConfirmModal(false);
    setCategoryToDelete(null);
  };

  // Table headers
  const headers = [
    {
      prop: "name",
      title: "Category Name",
      isSortable: true,
      isFilterable: true,
      cell: (row) => (
        <Link
          to={`/category/details/${row.id}`}
          className="text-decoration-none fw-bold text-primary"
          style={{ cursor: "pointer" }}
        >
          {row.name}
        </Link>
      ),
    },
    {
      prop: "image_url",
      title: "Category Image",
      cell: (row) => (
        <img
          src={row.image_url || "/not-img-found.png"}
          alt={row.name}
          style={{
            width: "40px",
            height: "40px",
            objectFit: "cover",
            borderRadius: "4px",
            marginRight: "12px",
          }}
          onError={(e) => {
            e.target.onerror = null;
            e.target.src = "/not-img-found.png";
          }}
        />
      ),
    },
    {
      prop: "description",
      title: "Description",
      cell: (row) => (
        <div
          style={{
            maxWidth: "300px",
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
          }}
          title={row.description}
        >
          {row.description || "No description"}
        </div>
      ),
    },
    {
      prop: "status",
      title: "Status",
      isSortable: true,
      isFilterable: true,
      cell: (row) => (
        <Badge bg={row.status === "Active" ? "success" : "secondary"}>
          {row.status}
        </Badge>
      ),
    },
    {
      prop: "created_at",
      title: "Created Date",
      isFilterable: true,
      isSortable: true,
      cell: (row) => {
        const { formatDateToDDMMYYYY } = require("../../utils/dateUtils");
        return formatDateToDDMMYYYY(row.created_at) || "-";
      },
    },
    {
      prop: "actions",
      title: "Actions",
      cell: (row) => (
        <div className="d-flex gap-2">
          <Button
            size="sm"
            variant="outline-dark"
            onClick={() => handleEditCategory(row.id)}
            disabled={isUpdating || isDeleting}
          >
            <FaPencilAlt />
          </Button>
          <Button
            size="sm"
            variant="outline-danger"
            onClick={() => handleDeleteCategory(row.id, row.name)}
            disabled={isUpdating || isDeleting}
          >
            <FaRegTrashAlt />
          </Button>
        </div>
      ),
    },
  ];

  if (isLoading && categories.length === 0) {
    return <div className="text-center mt-4">Loading categories...</div>;
  }

  const labels = {
    beforeSelect: " ",
  };

  return (
    <div className="page-container">
      <CategoryModal
        show={showModal}
        handleClose={handleCloseModal}
        editId={editingCategory}
      />

      <ConfirmModal
        show={showConfirmModal}
        onHide={cancelDeleteCategory}
        onConfirm={confirmDeleteCategory}
        title="Delete Category"
        message={`Are you sure you want to delete the category "${categoryToDelete?.name}"? This action cannot be undone.`}
        confirmText="Delete"
        cancelText="Cancel"
        variant="danger"
        isLoading={isDeleting}
      />

      <div className="d-flex justify-content-between align-items-center mb-3">
        <Link className="breadcrum" to="/">
          Home{"/"}
          <i
            className="fa-solid fa-chevron-right"
            style={{ marginLeft: "1px", marginRight: "1px" }}
          ></i>{" "}
          Categories
        </Link>
      </div>

      <DatatableWrapper
        body={categories || []}
        headers={headers || []}
        paginationOptionsProps={{
          initialState: { rowsPerPage: 5, options: [5, 10, 15, 20] },
        }}
      >
        <div className="compact-controls d-flex align-items-center justify-content-between flex-wrap gap-2 mb-2">
          <div className="d-flex align-items-center">
            <div className="flex-grow-1 me-2" style={{ maxWidth: "300px" }}>
              <Filter placeholder="Search categories..." className="w-100 " />
            </div>
            <PaginationOptions labels={labels} />
            <InfoPill left="Total Categories" right={categories?.length} />
          </div>

          <div className="d-flex align-items-center">
            <Button
              onClick={handleCreateCategory}
              variant="dark"
              className="btn-sm"
            >
              New Category
            </Button>
          </div>
        </div>

        {/* Table */}
        <div className="table-responsive shadow-sm">
          <table className="table table-striped table-bordered align-middle">
            <TableHeader />
            <TableBody />
          </table>
        </div>

        {/* Pagination */}
        <div className="compact-pagination d-flex justify-content-left mt-3">
          <Pagination classes={{
            button: "bg-dark",
          }} />
        </div>
      </DatatableWrapper>
    </div>
  );
};

export default Category;